import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, RefreshCw, MapPin, User, Calendar, Eye, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AdminNav from "@/components/AdminNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";

const STATUS_STYLES = {
  scheduled: "bg-amber-100 text-amber-700 border-amber-200",
  assigned: "bg-sky/15 text-sky border-sky/30",
  in_progress: "bg-violet-100 text-violet-700 border-violet-200",
  completed: "bg-crop/15 text-crop border-crop/30",
  invoiced: "bg-orange-100 text-orange-700 border-orange-200",
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  cancelled: "bg-rose-100 text-rose-700 border-rose-200",
};

export default function WorkOrdersList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, revenue_paid: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/work-orders", { params: filter !== "all" ? { status_filter: filter } : {} }),
        api.get("/admin/work-orders/stats"),
      ]);
      setItems(a.data);
      setStats(b.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("aero_token");
        navigate("/login");
      } else toast.error("Failed to load work orders");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const remove = async (id) => {
    if (!window.confirm("Delete this work order? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/work-orders/${id}`);
      toast.success("Work order deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-work-orders-page" className="min-h-screen bg-muted/30">
      <AdminNav />
      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl text-primary font-bold">Work orders</h1>
            <p className="text-muted-foreground mt-1">Schedule, assign and track every drone job.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={load} variant="outline" size="sm" data-testid="reload-btn" className="rounded-full">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button asChild data-testid="new-wo-btn" className="rounded-full bg-primary hover:bg-secondary text-primary-foreground">
              <Link to="/admin/work-orders/new"><Plus className="h-4 w-4 mr-1.5" /> New work order</Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total jobs" value={stats.total} testid="stat-total" />
          <StatCard label="In progress" value={stats.by_status?.in_progress || 0} testid="stat-progress" />
          <StatCard label="Completed" value={(stats.by_status?.completed || 0) + (stats.by_status?.invoiced || 0) + (stats.by_status?.paid || 0)} testid="stat-completed" />
          <StatCard label="Revenue collected" value={`$${(stats.revenue_paid || 0).toLocaleString()}`} testid="stat-revenue" highlight />
        </div>

        <div className="bg-card border border-border rounded-3xl shadow-card overflow-hidden">
          <div className="p-5 border-b border-border flex items-center justify-between flex-wrap gap-3">
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList data-testid="status-tabs">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
                <TabsTrigger value="assigned">Assigned</TabsTrigger>
                <TabsTrigger value="in_progress">In progress</TabsTrigger>
                <TabsTrigger value="completed">Completed</TabsTrigger>
                <TabsTrigger value="invoiced">Invoiced</TabsTrigger>
                <TabsTrigger value="paid">Paid</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="text-sm text-muted-foreground">{items.length} job{items.length !== 1 ? "s" : ""}</div>
          </div>

          {items.length === 0 ? (
            <div data-testid="empty-state" className="py-20 text-center text-muted-foreground">
              {loading ? <Loader2 className="h-5 w-5 animate-spin inline" /> : "No work orders here yet."}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {items.map((w, i) => (
                <div key={w.id} data-testid={`wo-row-${i}`} className="p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4 hover:bg-muted/30 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`border ${STATUS_STYLES[w.status] || "bg-muted text-foreground"} text-xs`}>{w.status.replace("_", " ")}</Badge>
                      {w.priority === "rush" && <Badge className="bg-accent/15 text-accent border-accent/30 text-xs">RUSH</Badge>}
                      <span className="font-display text-lg font-bold text-primary">{w.client_name}</span>
                      {w.farm_name && <span className="text-sm text-muted-foreground">· {w.farm_name}</span>}
                    </div>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1 truncate"><MapPin className="h-3 w-3 shrink-0" /> {w.location_address}</span>
                      <span className="flex items-center gap-1"><User className="h-3 w-3" /> {w.assigned_pilot_name || "Unassigned"}</span>
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {w.scheduled_date || "—"}</span>
                      <span>{w.service}{w.acres ? ` · ${w.acres} acres` : ""}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end lg:self-auto">
                    <Button asChild variant="outline" size="sm" data-testid={`wo-open-${i}`} className="rounded-full">
                      <Link to={`/admin/work-orders/${w.id}`}><Eye className="h-4 w-4 mr-1.5" /> Open</Link>
                    </Button>
                    <Button onClick={() => remove(w.id)} variant="ghost" size="icon" data-testid={`wo-delete-${i}`} className="text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const StatCard = ({ label, value, testid, highlight }) => (
  <div data-testid={testid} className={`rounded-2xl border p-5 shadow-card ${highlight ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}>
    <div className={`font-display text-2xl md:text-3xl font-bold ${highlight ? "" : "text-primary"}`}>{value}</div>
    <div className={`text-xs uppercase tracking-wider mt-1 ${highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{label}</div>
  </div>
);
